import { useState } from 'react';
import { IconDownload, IconFileInvoice } from '@tabler/icons-react';
import { Row, Col, Card } from 'antd';
import { ChildId, SummaryInvoiceDataDto } from 'types';
import { AppButton, AppFlex, AppText } from '../../../../../../atoms';
import { settlementTexts } from '../../../../../../../consts/text';
import { useSimpleNotification } from '../../../../../../../hooks/useSimpleNotification/useSimpleNotification';
import ParentHTTPService from '../../../../../../../services/HTTPService/parent/ParentHTTPService';
import StaffHTTPService from '../../../../../../../services/HTTPService/staff/StaffHTTPService';
import { downloadFileFromHex } from '../../../../../../../utils/fileDownload';

type AdditionalCostInvoiceProps = {
    invoiceData: SummaryInvoiceDataDto;
    childId?: ChildId;
    isParent?: boolean;
    title?: string;
};

export const AdditionalCostInvoice = ({
    invoiceData,
    childId,
    isParent = false,
    title = 'Rozliczenie główne',
}: AdditionalCostInvoiceProps) => {
    const { displayError } = useSimpleNotification();
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);

    const handleDownloadInvoice = async (invoiceId: string, fileName: string) => {
        try {
            setDownloadingInvoiceId(invoiceId);
            const response =
                isParent && childId
                    ? await ParentHTTPService.invoices.printInvoice({ invoiceId, childId })
                    : await StaffHTTPService.invoices.printInvoice(invoiceId);

            // The response is configured as arraybuffer, but may come as hex string
            // Convert the response data to hex string if it's not already
            const hexData =
                typeof response.data === 'string'
                    ? response.data
                    : Array.from(new Uint8Array(response.data))
                          .map((byte) => byte.toString(16).padStart(2, '0'))
                          .join('');

            downloadFileFromHex(hexData, `${fileName}.pdf`);
        } catch (error) {
            displayError(settlementTexts.children.invoices.downloadError);
            console.error('Error downloading invoice:', error);
        } finally {
            setDownloadingInvoiceId(null);
        }
    };

    return (
        <div style={{ marginTop: 0 }}>
            <Row gutter={[16, 16]}>
                <Col xs={24} key={invoiceData.id}>
                    <Card
                        title={
                            <AppFlex align="center" gap="small">
                                <IconFileInvoice size={16} />
                                <AppText>{title}</AppText>
                            </AppFlex>
                        }
                        extra={
                            <AppButton
                                type="link"
                                size="small"
                                icon={<IconDownload size={16} />}
                                loading={downloadingInvoiceId === invoiceData.id}
                                onClick={() =>
                                    handleDownloadInvoice(invoiceData.id, invoiceData.invoiceNumber)
                                }
                            >
                                Pobierz
                            </AppButton>
                        }
                    >
                        <AppText textType="BodySmall" color="secondary">
                            {invoiceData.invoiceNumber}
                        </AppText>
                        <br />
                        <AppText textType="BodySmall" color="secondary">
                            Utworzono: {new Date(invoiceData.issueDate).toLocaleDateString('pl-PL')}
                        </AppText>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};
